import React, { useState } from 'react';
import { Link } from 'react-router';
import styled from 'styled-components';
import { LISTING_TYPE_OPTIONS, SELLER_LISTING_DESTINATIONS } from '../../constants/listingTypeOptions';

const InputGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const QuestionLabel = styled.p`
  margin: 0 0 0.65rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;
`;

const YesNoRow = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const YesNoOption = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  border-radius: 0.6rem;
  border: 2px solid ${(p) => (p.$active ? '#1b4332' : '#d1d5db')};
  background: ${(p) => (p.$active ? '#e8f5ee' : '#fff')};
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
  font-size: 0.95rem;
  font-weight: ${(p) => (p.$active ? '700' : '500')};
  color: ${(p) => (p.$active ? '#14532d' : '#6b7280')};
  min-width: 5rem;

  &:hover {
    border-color: ${(p) => (p.$active ? '#1b4332' : '#9ca3af')};
  }
`;

const Radio = styled.span`
  flex-shrink: 0;
  width: 1.2rem;
  height: 1.2rem;
  border-radius: 50%;
  border: 2px solid ${(p) => (p.$active ? '#1b4332' : '#9ca3af')};
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RadioDot = styled.span`
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background: #1b4332;
`;

const PreOwnedSection = styled.div`
  margin-top: 0.5rem;
`;

const PreOwnedHeading = styled.p`
  margin: 0 0 0.65rem;
  font-size: 0.82rem;
  font-weight: 700;
  color: #1b4332;
`;

const RadioGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.65rem;

  @media (min-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const RadioOption = styled.label`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 0.75rem;
  min-height: 4.25rem;
  padding: 0.9rem 1rem;
  border-radius: 0.75rem;
  border: 2px solid ${(p) => (p.$active ? '#1b4332' : '#d1d5db')};
  background: ${(p) => (p.$active ? '#e8f5ee' : '#fff')};
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
  color: #374151;
  text-align: start;
  line-height: 1.35;

  &:hover {
    border-color: ${(p) => (p.$active ? '#1b4332' : '#9ca3af')};
  }

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
`;

const CheckBox = styled.span`
  flex-shrink: 0;
  width: 1.35rem;
  height: 1.35rem;
  margin-top: 0.12rem;
  border-radius: 0.28rem;
  border: 2px solid ${(p) => (p.$active ? '#1b4332' : '#9ca3af')};
  background: ${(p) => (p.$active ? '#1b4332' : '#fff')};
  color: #f6f1e8;
  font-size: 0.82rem;
  font-weight: 800;
  line-height: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OptionCopy = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.28rem;
  min-width: 0;
`;

const OptionTitle = styled.span`
  font-size: 0.95rem;
  font-weight: ${(p) => (p.$active ? '700' : '600')};
  color: ${(p) => (p.$active ? '#14532d' : '#1f2937')};
`;

const OptionDescription = styled.span`
  font-size: 0.78rem;
  font-weight: 450;
  color: #6b7280;
  line-height: 1.4;
`;

const SellerGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.55rem;

  @media (min-width: 720px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const SellerCard = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.75rem 0.85rem;
  border-radius: 0.75rem;
  border: 1.5px solid #d8e2dc;
  background: #f6f1e8;
  text-decoration: none;
  color: #1b4332;

  &:hover {
    border-color: #1b4332;
    color: #1b4332;
  }
`;

const SellerTitle = styled.span`
  font-size: 0.88rem;
  font-weight: 700;
`;

const SellerDesc = styled.span`
  font-size: 0.74rem;
  color: #6b7c72;
  line-height: 1.35;
`;

export default function ListingTypeSelector({ value, onChange }) {
  const [isBusinessOwner, setIsBusinessOwner] = useState(null);

  return (
    <>
      {/* Business Owner Question */}
      <InputGroup>
        <QuestionLabel>
          Are you a Business owner of an online Boutique, Art Gallery, Designer Shop?
        </QuestionLabel>
        <YesNoRow>
          <YesNoOption
            type="button"
            $active={isBusinessOwner === false}
            onClick={() => setIsBusinessOwner(false)}
          >
            <Radio $active={isBusinessOwner === false}>
              {isBusinessOwner === false && <RadioDot />}
            </Radio>
            No
          </YesNoOption>
          <YesNoOption
            type="button"
            $active={isBusinessOwner === true}
            onClick={() => setIsBusinessOwner(true)}
          >
            <Radio $active={isBusinessOwner === true}>
              {isBusinessOwner === true && <RadioDot />}
            </Radio>
            Yes
          </YesNoOption>
        </YesNoRow>
      </InputGroup>

      {/* Seller Type Selection (only when Yes is selected) */}
      {isBusinessOwner === true && (
        <InputGroup>
          <QuestionLabel>Which one are you?</QuestionLabel>
          <SellerGrid>
            {SELLER_LISTING_DESTINATIONS.map((dest) => (
              <SellerCard key={dest.id} to={dest.path}>
                <SellerTitle>{dest.title}</SellerTitle>
                <SellerDesc>{dest.description}</SellerDesc>
              </SellerCard>
            ))}
          </SellerGrid>
        </InputGroup>
      )}

      {/* Pre-Owned Listing Types */}
      <PreOwnedSection>
        <PreOwnedHeading>Or list a pre-owned item</PreOwnedHeading>
        <RadioGroup role="radiogroup" aria-label="Listing type">
          {LISTING_TYPE_OPTIONS.map((option) => {
            const active = value === option.id;
            return (
              <RadioOption key={option.id} $active={active}>
                <input
                  type="checkbox"
                  name="listing_type"
                  value={option.id}
                  checked={active}
                  onChange={() => onChange(option.id)}
                />
                <CheckBox $active={active} aria-hidden>
                  {active ? '✓' : ''}
                </CheckBox>
                <OptionCopy>
                  <OptionTitle $active={active}>{option.title || option.label}</OptionTitle>
                  {option.description ? (
                    <OptionDescription>{option.description}</OptionDescription>
                  ) : null}
                </OptionCopy>
              </RadioOption>
            );
          })}
        </RadioGroup>
      </PreOwnedSection>
    </>
  );
}
